/**
 * useClipboardPaste — intercept Cmd+V outside input fields to create tasks.
 *
 * Behavior:
 * - Single line → create task immediately (toast notification)
 * - Multi-line → show confirmation toast with count
 * - Blocked in Logbook/Trash views
 * - If a task is selected → insert after selected task
 * - Otherwise → append at bottom of current context
 *
 * Only fires when NO input/textarea/contenteditable is focused.
 */

import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { useViewContext } from "@/app/stores/viewContext"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { pasteTasksFromText } from "@/shared/utils/task/pasteTasks"

// ---------------------------------------------------------------------------
// Guard: is an input-like element focused?
// ---------------------------------------------------------------------------

function isInputFocused(): boolean {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  if (tag === "input" || tag === "textarea" || tag === "select") return true
  if ((el as HTMLElement).isContentEditable) return true
  // Also check for common rich-text editors
  if (el.closest("[contenteditable]")) return true
  return false
}

// ---------------------------------------------------------------------------
// Non-creatable views — paste blocked
// ---------------------------------------------------------------------------

const BLOCKED_VIEWS = new Set(["logbook", "trash", "search"])

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useClipboardPaste(): void {
  const { t } = useTranslation()
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)
  const eveningHourRef = useRef(eveningHour)
  eveningHourRef.current = eveningHour

  useEffect(() => {
    const handler = async (e: ClipboardEvent) => {
      // Skip if input is focused
      if (isInputFocused()) return

      const state = useViewContext.getState()

      // Skip if view doesn't allow creation
      if (BLOCKED_VIEWS.has(state.viewId)) return
      if (!state.allowCreate) return

      const text = e.clipboardData?.getData("text/plain")
      if (!text?.trim()) return

      // Prevent default browser paste
      e.preventDefault()

      // Determine insert position: after selected task, or "top"
      const selectedIds = state.selectedTaskIds
      const insertAfter = selectedIds.length === 1 ? selectedIds[0] : undefined

      // Shared paste path: view defaults (parent + schedule), atomic creation,
      // clipboard order kept, one toast, errors shown
      try {
        await pasteTasksFromText(text, {
          viewId: state.viewId,
          containerId: state.containerId,
          eveningHour: eveningHourRef.current,
          insertPosition: insertAfter ? { afterId: insertAfter } : "top",
        })
      } catch (error) {
        console.error("[clipboard-paste] Failed to create tasks:", error)
        toast.error(t("Failed to paste tasks"))
      }
    }

    document.addEventListener("paste", handler)
    return () => document.removeEventListener("paste", handler)
  }, [t])
}

